import { assertUnreachable } from '@sgme/fp';
import type { ISalesCounterpartFetchApi } from 'apiConnector/counterpart/counterpart';
import type { UserPreferenceAnalyticAction } from 'models';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { UnderlyingCodeType } from 'models/underlying/base';
import { connect } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { Region } from 'state/discover/page/page.model';
import type { ProductCodeType } from 'state/secondaryMarket/productSearch';
import {
  areUserPreferencesReady,
  getIsUserInternal,
  getSpmHiddenTabs,
  getSpmVisibleTabs,
  getUserPreferencesAvailableCurrencies,
  getUserPreferencesAvailableDiscoverPageRegions,
  getUserPreferencesAvailableProductSubtypes,
  getUserPreferencesAvailableProductTypes,
  getUserPreferencesAvailableWrappers,
  getUserPreferencesBatchWorkspaceVersion,
  getUserPreferencesCurrency,
  getUserPreferencesDiscoverPageRegion,
  getUserPreferencesLandingPage,
  getUserPreferencesNotifications,
  getUserPreferencesProductSubtype,
  getUserPreferencesProductType,
  getUserPreferencesQuoteWorkspaceVersion,
  getUserPreferencesSecondaryMarketCloseTabWhenOrderPlacedActivated,
  getUserPreferencesSecondaryMarketProductSearchCodeType,
  getUserPreferencesSelectedOnBehalfClient,
  getUserPreferencesUnderlyingCodeType,
  getUserPreferencesWrapper,
  getUserPreferencesXMargin,
  isUserPreferencesPrivateMode,
  userPreferencesValidation,
} from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import {
  userPreferencesSpmTabsOrderReset,
  userPreferencesSpmTabsUpdated,
} from 'state/userPreferences/tabsOrder/tabOrder.action';
import {
  userPreferencesBatchWorkspaceVersionChanged,
  userPreferencesDefaultFieldValuesChanged,
  userPreferencesDiscoverPageRegionChanged,
  userPreferencesLandingPageChanged,
  userPreferencesNotificationContactsChanged,
  userPreferencesQuoteWorkspaceVersionChanged,
  userPreferencesSecondaryMarketIsAutomaticTabCloseChanged,
  userPreferencesSecondaryMarketProductSearchCodeTypeChanged,
  userPreferencesUnderlyingCodeTypeChanged,
} from 'state/userPreferences/userPreferences.action';
import type {
  BatchWorkspaceVersion,
  DefaultFieldValues,
  LandingPage,
  Notifications,
  QuoteWorkspaceVersion,
} from 'state/userPreferences/userPreferences.model';

interface OwnProps {
  onClose: () => void;
  clientSearchApi: ISalesCounterpartFetchApi;
}

interface StateProps {
  isLoaded: boolean;
  landingPage?: LandingPage;
  quoteWorkspaceVersion: QuoteWorkspaceVersion;
  batchWorkspaceVersion: BatchWorkspaceVersion;
  discoverPageRegion?: Region;
  availableDiscoverPageRegions: readonly Region[];
  isPrivateMode: boolean;
  isUserInternal: boolean;
  selectedOnBehalfClient?: ISalesCounterpart;
  availableProductTypes: readonly string[];
  productType?: string;
  productTypeValidity: userPreferencesValidation.ValidationStatus;
  availableProductSubtypes: readonly string[];
  productSubtype?: string;
  productSubTypeValidity: userPreferencesValidation.ValidationStatus;
  availableWrappers: readonly string[];
  wrapper?: string;

  wrapperValidity: userPreferencesValidation.ValidationStatus;
  currency?: string | null;
  availableCurrencies: ReadonlyArray<string | null>;
  currencyValidity: userPreferencesValidation.ValidationStatus;

  productTabErrorCount: number;
  xMargin?: Nullable<number>;
  defaultUnderlyingCodeType: UnderlyingCodeType;
  hasUnderlyingTypeCode: boolean;

  hiddenSpmTabs: readonly string[];
  visibleSpmTabs: readonly string[];
  defaultProductSearchCodeType: ProductCodeType;
  closeTabWhenLiveTradeCpOrderIsPlacedActivated: boolean;
  notifications?: Notifications;
}

export type DefaultFieldKey = keyof DefaultFieldValues;

interface DispatchProps {
  onLandingPageChanged(value: LandingPage): void;
  onQuoteWorkspaceVersionChanged(value: QuoteWorkspaceVersion): void;
  onBatchWorkspaceVersionChanged(value: BatchWorkspaceVersion): void;
  onDefaultFieldChanged<K extends DefaultFieldKey>(key: K, value: DefaultFieldValues[K]): void;
  onUnderlyingCodeTypeChanged(value: UnderlyingCodeType): void;
  onSpmTabsUpdated(visibleTabs: string[], hiddenTabs: string[]): void;
  onResetSpmOrder(): void;

  onDiscoverPageRegionChanged: (value: Region) => void;

  onProductSearchTypeChanged: (value: ProductCodeType) => void;
  onAutomaticTabCloseChanged: (value: boolean) => void;
  onNotificationContactsChanged: (value: Notifications) => void;
}

export type UserPreferencesFormProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps = (state: AppState): StateProps => {
  let availableWrappers = getUserPreferencesAvailableWrappers(state);
  const wrapper = getUserPreferencesWrapper(state);

  if (wrapper !== undefined && !availableWrappers.includes(wrapper)) {
    availableWrappers = [wrapper, ...availableWrappers];
  }
  let currencyValidity: userPreferencesValidation.ValidationStatus;
  let currency: string | null | undefined;
  const preferencesProductType = getUserPreferencesProductType(state);
  if (preferencesProductType === 'Accumulator' || preferencesProductType === 'FxDualCurrency') {
    currencyValidity = 'hidden';
    currency = null;
  } else {
    currencyValidity = userPreferencesValidation.getUserPreferencesCurrencyValidity(state);
    currency = getUserPreferencesCurrency(state);
  }

  return {
    landingPage: getUserPreferencesLandingPage(state),
    quoteWorkspaceVersion: getUserPreferencesQuoteWorkspaceVersion(state),
    batchWorkspaceVersion: getUserPreferencesBatchWorkspaceVersion(state),
    isLoaded: areUserPreferencesReady(state),
    isUserInternal: getIsUserInternal(state),
    isPrivateMode: isUserPreferencesPrivateMode(state),
    selectedOnBehalfClient: getUserPreferencesSelectedOnBehalfClient(state),
    availableProductTypes: getUserPreferencesAvailableProductTypes(state),
    productType: getUserPreferencesProductType(state),
    productTypeValidity: userPreferencesValidation.getUserPreferencesProductTypeValidity(state),
    availableProductSubtypes: getUserPreferencesAvailableProductSubtypes(state),
    productSubtype: getUserPreferencesProductSubtype(state),
    productSubTypeValidity: userPreferencesValidation.getUserPreferencesProductSubTypeValidity(state),
    availableWrappers,
    wrapper,
    wrapperValidity: userPreferencesValidation.getUserPreferencesWrapperValidity(state),
    availableCurrencies: getUserPreferencesAvailableCurrencies(state),
    currency,
    currencyValidity,
    productTabErrorCount: userPreferencesValidation.getUserPreferencesProducTabErrorCount(state),
    xMargin: getUserPreferencesXMargin(state),
    hasUnderlyingTypeCode: true,
    defaultUnderlyingCodeType: getUserPreferencesUnderlyingCodeType(state),
    visibleSpmTabs: getSpmVisibleTabs(state),
    hiddenSpmTabs: getSpmHiddenTabs(state),
    discoverPageRegion: getUserPreferencesDiscoverPageRegion(state),
    availableDiscoverPageRegions: getUserPreferencesAvailableDiscoverPageRegions(state),
    defaultProductSearchCodeType: getUserPreferencesSecondaryMarketProductSearchCodeType(state),
    closeTabWhenLiveTradeCpOrderIsPlacedActivated:
      getUserPreferencesSecondaryMarketCloseTabWhenOrderPlacedActivated(state),
    notifications: getUserPreferencesNotifications(state),
  };
};

function getAnalyticEventForQuoteWorkspaceVersion(
  workspaceVersion: QuoteWorkspaceVersion,
): UserPreferenceAnalyticAction {
  switch (workspaceVersion) {
    case 'Advanced':
      return 'Switch to advanced quote workspace';
    case 'Standard':
      return 'Switch to standard quote workspace';
    default:
      return assertUnreachable(workspaceVersion, 'Unhandled quote workspace version');
  }
}

function getAnalyticEventForBatchWorkspaceVersion(
  workspaceVersion: BatchWorkspaceVersion,
): UserPreferenceAnalyticAction {
  switch (workspaceVersion) {
    case 'Advanced':
      return 'Switch to advanced batch workspace';
    case 'Standard':
      return 'Switch to standard batch workspace';
    default:
      return assertUnreachable(workspaceVersion, 'Unhandled batch workspace version');
  }
}

function mapDispatchToProps(dispatch: ThunkDispatch): DispatchProps {
  return {
    onNotificationContactsChanged(value) {
      dispatch(userPreferencesNotificationContactsChanged(value));
    },
    onLandingPageChanged(value) {
      dispatch(userPreferencesLandingPageChanged(value));
    },
    onQuoteWorkspaceVersionChanged(value) {
      logAnalytics.trackEvent('User preferences', getAnalyticEventForQuoteWorkspaceVersion(value));
      dispatch(userPreferencesQuoteWorkspaceVersionChanged(value));
    },
    onBatchWorkspaceVersionChanged(value) {
      logAnalytics.trackEvent('User preferences', getAnalyticEventForBatchWorkspaceVersion(value));
      dispatch(userPreferencesBatchWorkspaceVersionChanged(value));
    },
    onDefaultFieldChanged(key, value) {
      dispatch(userPreferencesDefaultFieldValuesChanged(key, value));
    },
    onUnderlyingCodeTypeChanged(value) {
      dispatch(userPreferencesUnderlyingCodeTypeChanged(value));
    },
    onSpmTabsUpdated(visibleTabs: string[], hiddenTabs: string[]) {
      dispatch(userPreferencesSpmTabsUpdated(visibleTabs, hiddenTabs));
    },
    onResetSpmOrder() {
      dispatch(userPreferencesSpmTabsOrderReset());
    },
    onDiscoverPageRegionChanged(value: Region) {
      dispatch(userPreferencesDiscoverPageRegionChanged(value));
    },
    onProductSearchTypeChanged(value: ProductCodeType) {
      dispatch(userPreferencesSecondaryMarketProductSearchCodeTypeChanged(value));
    },
    onAutomaticTabCloseChanged(value: boolean) {
      dispatch(userPreferencesSecondaryMarketIsAutomaticTabCloseChanged(value));
    },
  };
}

export const connectUserPreferencesForm = connect(mapStateToProps, mapDispatchToProps);
